<script lang="ts">
  import { Card } from '$lib/components/ui/card';
  import PhilatelicItemRow from './PhilatelicItemRow.svelte';
  import { CatalogViewModel } from './CatalogViewModel.svelte';
  import { t } from '$lib/i18n';
  import { typeIndicatorClass } from './typeColors';
  import { PhilatelicItemType } from '@briefmarken_katalog/types';
  import type { TranslationKey } from '$lib/i18n';
  import VirtualList from '$lib/components/VirtualList.svelte';
  import { USE_VIRTUAL_LIST } from '../featureFlags';
  import { isNarrowViewport } from '$lib/stores/device';
  import { ChevronRight } from '@lucide/svelte';
  import { slide } from 'svelte/transition';
  import { Switch } from '$lib/components/ui/switch';
  import { Label } from '$lib/components/ui/label';
  import { Input } from '$lib/components/ui/input';
  import { Button } from '$lib/components/ui/button';

  const model = new CatalogViewModel();
  let filterExpanded = $state(false);

  // Stacked mobile rows are taller; must match the .item-row-contain heights in row.css
  const MOBILE_ROW_HEIGHT = 112;
  const DESKTOP_ROW_HEIGHT = 56;

  const allItemTypes: { type: PhilatelicItemType; key: TranslationKey }[] = [
    { type: PhilatelicItemType.SingleStamp, key: 'search.singleStamp' },
    { type: PhilatelicItemType.Set,         key: 'menu.set' },
    { type: PhilatelicItemType.StampFromSet, key: 'search.stampFromSet' },
    { type: PhilatelicItemType.Block,        key: 'menu.block' },
    { type: PhilatelicItemType.Booklet,      key: 'menu.booklet' },
    { type: PhilatelicItemType.SeTenants,    key: 'menu.seTenants' },
  ];

  function toggleItemType(type: PhilatelicItemType, checked: boolean) {
    model.itemFilter.itemTypes = checked
      ? [...model.itemFilter.itemTypes, type]
      : model.itemFilter.itemTypes.filter((t: PhilatelicItemType) => t !== type);
  }
</script>

<div class="flex flex-col h-full">
  <Card class="flex flex-col overflow-hidden card-3d flex-1 min-h-0">
    <!-- Title bar -->
    <div class="p-2 bg-gray-100 border-b font-bold card-3d flex items-center gap-1">
      {$t('mode.catalogMode')}
      {#if model.items !== null}
        <span class="font-normal text-sm text-gray-500">
          ({#if model.itemFilter.enabled}{model.visibleItems?.length} / {/if}{model.items.length})
        </span>
      {/if}
    </div>

    <!-- Collapsible filter header -->
    <button
      class="w-full px-3 py-1.5 bg-gray-50 border-b flex items-center gap-2 hover:bg-gray-100 text-sm text-left cursor-pointer"
      onclick={() => filterExpanded = !filterExpanded}
    >
      <ChevronRight class="w-4 h-4 shrink-0 transition-transform duration-150 {filterExpanded ? 'rotate-90' : ''}" />
      <span class="font-medium">{$t('search.title')}</span>
      {#if model.filterSummary}
        <span class="text-xs text-blue-600 truncate">{model.filterSummary}</span>
      {:else}
        <span class="text-xs text-gray-400">{$t('search.disabled')}</span>
      {/if}
    </button>

    <!-- Inline filter panel -->
    {#if filterExpanded}
    <div class="px-3 py-2 bg-gray-50 border-b flex flex-col gap-3 text-sm" transition:slide={{ duration: 300 }}>
      <!-- Enable/disable toggle -->
      <div class="flex items-center gap-2">
        <Switch bind:checked={model.itemFilter.enabled} />
        <Label>{model.itemFilter.enabled ? $t('search.enabled') : $t('search.disabled')}</Label>
      </div>

      <!-- Date range -->
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <Label for="cat-date-from">{$t('search.dateFrom')}</Label>
          <Input id="cat-date-from" type="date" bind:value={model.itemFilter.dateFrom}
                 disabled={!model.itemFilter.enabled} />
        </div>
        <div class="flex flex-col gap-1">
          <Label for="cat-date-to">{$t('search.dateTo')}</Label>
          <Input id="cat-date-to" type="date" bind:value={model.itemFilter.dateTo}
                 disabled={!model.itemFilter.enabled} />
        </div>
      </div>

      <!-- Description words -->
      <div class="flex flex-col gap-1">
        <Label for="cat-desc">{$t('search.descriptionWords')}</Label>
        <Input id="cat-desc" type="text" bind:value={model.itemFilter.descriptionWords}
               placeholder={$t('search.descriptionWordsPlaceholder')}
               disabled={!model.itemFilter.enabled} />
      </div>

      <!-- Zhihao -->
      <div class="flex flex-col gap-1">
        <Label for="cat-zhihao">{$t('zhihao.button')}</Label>
        <Input id="cat-zhihao" type="text" bind:value={model.itemFilter.zhihao}
               placeholder={$t('search.zhihaoPlaceholder')}
               disabled={!model.itemFilter.enabled} />
      </div>

      <!-- Item type checkboxes -->
      <div class="flex flex-col gap-1">
        <Label>{$t('search.itemTypes')}</Label>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-1">
          {#each allItemTypes as { type, key }}
          <div class="flex items-center gap-1">
            <input type="checkbox"
                   checked={model.itemFilter.itemTypes.includes(type)}
                   onchange={(e) => toggleItemType(type, e.currentTarget.checked)}
                   disabled={!model.itemFilter.enabled} />
            <span>{$t(key)}</span>
          </div>
          {/each}
        </div>
      </div>

      <!-- Reset -->
      <div>
        <Button variant="outline" onclick={() => model.resetItemFilter()}>
          {$t('search.reset')}
        </Button>
      </div>
    </div>
    {/if}

    <VirtualList items={model.visibleItems ?? []} virtual={USE_VIRTUAL_LIST}
                 itemHeight={$isNarrowViewport ? MOBILE_ROW_HEIGHT : DESKTOP_ROW_HEIGHT}>
      {#snippet children(item)}
        <PhilatelicItemRow {item} readonly={true} />
      {/snippet}
    </VirtualList>
  </Card>

  <div class="shrink-0 flex flex-wrap gap-x-4 gap-y-1 px-1 py-2 text-xs text-muted-foreground">
    <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm {typeIndicatorClass[PhilatelicItemType.SingleStamp]}"></span>{$t('search.singleStamp')}</span>
    <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm {typeIndicatorClass[PhilatelicItemType.Set]}"></span>{$t('menu.set')}</span>
    <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm {typeIndicatorClass[PhilatelicItemType.Block]}"></span>{$t('menu.block')}</span>
    <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm {typeIndicatorClass[PhilatelicItemType.Booklet]}"></span>{$t('menu.booklet')}</span>
    <span class="flex items-center gap-1"><span class="inline-block w-3 h-3 rounded-sm {typeIndicatorClass[PhilatelicItemType.SeTenants]}"></span>{$t('menu.seTenants')}</span>
  </div>
</div>
